import { useState } from 'react';
import { Mail } from 'lucide-react';

import { BottomSheet } from '@/components/ui/bottom-sheet';
import { ButtonSpinner } from '@/shared/components/button-spinner';
import { cn } from '@/lib/utils';
import { isValidEmail } from '@/shared/lib/email';
import type { ProductPlan } from '@/features/products/types';
import { useCheckout } from '../hooks/use-checkout';

interface ApplePaySheetProps {
  plan: ProductPlan;
  email: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ApplePaySheet({
  plan,
  email: initialEmail,
  open,
  onClose,
  onSuccess,
}: ApplePaySheetProps) {
  const [email, setEmail] = useState(initialEmail);
  const valid = isValidEmail(email);
  const { status, payWithApplePay } = useCheckout(plan, email, { initCard: false });

  const handlePay = async () => {
    if (!valid) return;
    const ok = await payWithApplePay();
    if (ok) onSuccess();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Apple Pay">
      <div className="flex flex-col items-center gap-4 px-4 pb-2 pt-6">
        <p className="text-center text-base text-mf-neutral-600">
          Please enter your email address so that we can send you an invoice
        </p>
        <div className="flex h-[52px] w-full items-center gap-2 rounded-xl bg-white px-4 shadow-[0_0_16px_rgba(26,22,19,0.03)]">
          <Mail className="size-5 shrink-0 text-mf-neutral-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="h-full flex-1 bg-transparent text-base font-medium text-mf-neutral-900 outline-none placeholder:text-mf-neutral-400"
          />
        </div>
      </div>

      <div className="p-4 pt-2">
        <button
          type="button"
          onClick={handlePay}
          disabled={!valid || status === 'processing'}
          className={cn(
            'flex h-[52px] w-full items-center justify-center rounded-full text-base font-bold transition-opacity',
            valid
              ? 'bg-mf-neutral-900 text-white hover:opacity-90'
              : 'bg-mf-neutral-900/20 text-white',
          )}
        >
          {status === 'processing' ? <ButtonSpinner /> : 'Proceed payment'}
        </button>
      </div>
    </BottomSheet>
  );
}
